import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Link2, ExternalLink, CheckCircle, AlertTriangle, Loader2, Search, ChevronLeft,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";

export default function ListEbay() {
  const navigate = useNavigate();
  const [connection, setConnection] = useState(null);
  const [marketplaces, setMarketplaces] = useState([]);
  const [marketplace, setMarketplace] = useState("EBAY_GB");
  const [policies, setPolicies] = useState(null);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [cards, setCards] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [fulfillmentPolicyId, setFulfillmentPolicyId] = useState("");
  const [paymentPolicyId, setPaymentPolicyId] = useState("");
  const [returnPolicyId, setReturnPolicyId] = useState("");
  const [useReferencePlaceholder, setUseReferencePlaceholder] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/ebay/connection").then((r) => setConnection(r.data)).catch(() => setConnection({ connected: false }));
    api.get("/ebay/marketplaces").then((r) => setMarketplaces(r.data.marketplaces || [])).catch(() => {});
    api.get("/cards").then((r) => setCards(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!connection?.connected || !marketplace) return;
    setLoadingPolicies(true);
    setFulfillmentPolicyId(""); setPaymentPolicyId(""); setReturnPolicyId("");
    api.get(`/ebay/policies?marketplace_id=${marketplace}`)
      .then((r) => {
        setPolicies(r.data);
        // Auto-select the first policy of each kind so a solo-policy seller doesn't
        // have to click three dropdowns before hitting Create.
        if (r.data.fulfillment?.length === 1) setFulfillmentPolicyId(r.data.fulfillment[0].id);
        if (r.data.payment?.length === 1) setPaymentPolicyId(r.data.payment[0].id);
        if (r.data.return?.length === 1) setReturnPolicyId(r.data.return[0].id);
      })
      .catch(() => setPolicies({ fulfillment: [], payment: [], return: [] }))
      .finally(() => setLoadingPolicies(false));
  }, [connection, marketplace]);

  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards
      .filter((c) => (c.current_price ?? 0) > 0)  // only sellable cards
      .filter((c) => !q || (c.name || "").toLowerCase().includes(q) || (c.set_name || "").toLowerCase().includes(q));
  }, [cards, search]);

  const toggleCard = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const selectAllShown = () => {
    const next = new Set(selectedIds);
    filteredCards.slice(0, 50).forEach((c) => next.add(c.id));
    setSelectedIds(next);
    if (filteredCards.length > 50) toast.info("Selected first 50 — eBay allows up to 50 drafts per request");
  };
  const clearSelection = () => setSelectedIds(new Set());

  const missingPolicies = policies && (!policies.fulfillment?.length || !policies.payment?.length || !policies.return?.length);
  const canSubmit = connection?.connected && selectedIds.size > 0 && fulfillmentPolicyId && paymentPolicyId && returnPolicyId && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/ebay/drafts", {
        card_ids: Array.from(selectedIds),
        marketplace_id: marketplace,
        fulfillment_policy_id: fulfillmentPolicyId,
        payment_policy_id: paymentPolicyId,
        return_policy_id: returnPolicyId,
        use_reference_placeholder: useReferencePlaceholder,
      });
      setResults(res.data);
      const ok = (res.data.results || []).filter((r) => r.status === "draft_created").length;
      if (ok > 0) toast.success(`Created ${ok} draft${ok === 1 ? "" : "s"} in your eBay Seller Hub`);
      else toast.error("No drafts were created — see details below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create drafts");
    } finally {
      setSubmitting(false);
    }
  };

  if (connection && !connection.connected) {
    return (
      <div className="min-h-screen bg-[#05060f] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center border border-white/10 bg-[#111113] rounded-2xl p-8">
          <Link2 className="w-10 h-10 text-[#A78BFA] mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold mb-2">Connect eBay first</h1>
          <p className="text-sm text-zinc-400 mb-5">Link your eBay seller account in Settings before creating any drafts.</p>
          <Link to="/settings">
            <Button data-testid="list-ebay-goto-settings" className="bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full">
              Go to Settings
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/binder")} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white mb-4">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to binder
        </button>
        <h1 data-testid="list-ebay-title" className="font-display text-4xl sm:text-5xl font-bold mb-2 flex items-center gap-3">
          <Link2 className="w-8 h-8 text-[#A78BFA]" /> List on eBay
        </h1>
        <p className="text-zinc-400 text-sm mb-8">
          Select cards from your binder and we&apos;ll create <span className="text-violet-200 font-medium">unpublished drafts</span> in your eBay Seller Hub — you review and click Publish yourself.
        </p>

        {/* Marketplace picker */}
        <div className="border border-white/10 bg-[#111113] rounded-2xl p-5 mb-4">
          <p className="text-sm font-medium mb-3">Marketplace</p>
          <div className="flex flex-wrap gap-2">
            {marketplaces.map((m) => (
              <button
                key={m.id}
                data-testid={`list-ebay-marketplace-${m.id.toLowerCase()}`}
                onClick={() => setMarketplace(m.id)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors border ${
                  marketplace === m.id
                    ? "bg-[#A78BFA] text-black border-[#A78BFA]"
                    : "border-white/10 text-zinc-300 hover:bg-white/5"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Policies picker */}
        <div className="border border-white/10 bg-[#111113] rounded-2xl p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium">Listing templates <span className="text-xs text-zinc-500 font-normal">(shipping · payment · returns)</span></p>
            <a href={policies?.seller_hub_url || "https://www.ebay.co.uk/bp/manage"}
               target="_blank" rel="noopener noreferrer"
               className="text-xs text-zinc-500 hover:text-white flex items-center gap-1">
              Manage on eBay <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          {loadingPolicies && <p className="text-xs text-zinc-500"><Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Fetching your eBay templates…</p>}
          {policies && !loadingPolicies && missingPolicies && (
            <div data-testid="list-ebay-missing-policies" className="border border-amber-400/30 bg-amber-500/5 rounded-lg p-3 mb-3 text-xs text-amber-300 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  Your eBay account is missing one or more listing templates for <span className="font-mono-plex">{marketplace}</span>. eBay&apos;s API needs default shipping, payment and return terms attached to every listing — a one-time 3-minute setup.
                </div>
              </div>
              <div className="text-zinc-400 pl-6">
                <p className="mb-2">Despite the name, <span className="text-white">Business Policies</span> is available to every eBay seller — private individuals included. Your listings will still look and behave like any normal private-seller listing.</p>
              </div>
              <ol className="list-decimal pl-8 space-y-1 text-zinc-300">
                <li>
                  <a href={policies.opt_in_url} target="_blank" rel="noopener noreferrer" className="text-[#A78BFA] underline hover:text-white">
                    Opt in to Business Policies (private sellers welcome)
                  </a>
                </li>
                <li>
                  <a href={policies.seller_hub_url} target="_blank" rel="noopener noreferrer" className="text-[#A78BFA] underline hover:text-white">
                    Create your shipping, payment and return templates
                  </a>
                </li>
                <li>Come back here and refresh — the dropdowns will populate.</li>
              </ol>
            </div>
          )}
          {policies && !loadingPolicies && (
            <div className="grid sm:grid-cols-3 gap-3 text-xs">
              {[
                { label: "Shipping", options: policies.fulfillment || [], value: fulfillmentPolicyId, set: setFulfillmentPolicyId, tid: "fulfillment" },
                { label: "Payment", options: policies.payment || [], value: paymentPolicyId, set: setPaymentPolicyId, tid: "payment" },
                { label: "Returns", options: policies.return || [], value: returnPolicyId, set: setReturnPolicyId, tid: "return" },
              ].map((p) => (
                <label key={p.label} className="block">
                  <span className="block text-zinc-500 mb-1">{p.label}</span>
                  <select
                    data-testid={`list-ebay-policy-${p.tid}`}
                    value={p.value}
                    onChange={(e) => p.set(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">— select —</option>
                    {p.options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Card selection */}
        <div className="border border-white/10 bg-[#111113] rounded-2xl p-5 mb-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-sm font-medium">Cards ({selectedIds.size} selected)</p>
            <div className="flex gap-2">
              <button
                data-testid="list-ebay-select-all"
                onClick={selectAllShown}
                className="text-xs text-[#A78BFA] hover:text-white"
              >Select all shown</button>
              <button
                data-testid="list-ebay-clear-selection"
                onClick={clearSelection}
                className="text-xs text-zinc-500 hover:text-white"
              >Clear</button>
            </div>
          </div>
          <div className="mb-3 p-3 rounded-lg bg-black/30 border border-white/5 text-[11px] text-zinc-500 leading-relaxed space-y-3">
            <p><span className="text-zinc-300 font-medium">Photos:</span> only photos <span className="text-zinc-300">you&apos;ve added</span> are sent to eBay. For cards with no photo, you can either upload one first or use the AI reference image as a temporary placeholder you swap on eBay before publishing.</p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="list-ebay-use-placeholder"
                checked={useReferencePlaceholder}
                onChange={(e) => setUseReferencePlaceholder(e.target.checked)}
                className="mt-0.5 accent-[#A78BFA]"
              />
              <span className="text-zinc-300">
                <span className="text-white font-medium">Use the AI reference image as a placeholder</span> when I haven&apos;t added a photo.
                <span className="text-zinc-500"> The draft still lands unpublished — swap the image on eBay before publishing.</span>
              </span>
            </label>
          </div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              data-testid="list-ebay-search"
              placeholder="Search your priced cards…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-black/30 border-white/10 text-sm"
            />
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
            {filteredCards.length === 0 && (
              <div className="text-xs text-zinc-500 py-8 text-center">
                No priced cards match — eBay drafts need a market price to list at.
              </div>
            )}
            {filteredCards.map((c) => {
              const isSelected = selectedIds.has(c.id);
              return (
                <label
                  key={c.id}
                  data-testid={`list-ebay-card-${c.id}`}
                  className={`flex items-center gap-3 py-2 px-2 rounded cursor-pointer transition-colors ${isSelected ? "bg-[#A78BFA]/5" : "hover:bg-white/[0.02]"}`}
                >
                  <Checkbox
                    data-testid={`list-ebay-card-checkbox-${c.id}`}
                    checked={isSelected}
                    onCheckedChange={() => toggleCard(c.id)}
                  />
                  {c.captured_image_url ? (
                    <img src={c.captured_image_url} alt="" className="w-8 h-10 object-cover rounded" />
                  ) : c.reference_image_url ? (
                    <img src={c.reference_image_url} alt="" className="w-8 h-10 object-cover rounded opacity-40" />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate flex items-center gap-1.5">
                      {c.name}
                      {!c.captured_image_url && (!c.additional_images || c.additional_images.length === 0) && (
                        useReferencePlaceholder ? (
                          <span data-testid={`list-ebay-placeholder-tag-${c.id}`} title="AI reference image will be used as a placeholder — swap on eBay before publishing" className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#A78BFA]/10 text-[#A78BFA] border border-[#A78BFA]/20">
                            AI placeholder
                          </span>
                        ) : (
                          <span data-testid={`list-ebay-no-photo-${c.id}`} title="Enable AI placeholder above or add your own photo before listing" className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-500/10 text-amber-300 border border-amber-400/20">
                            no photo
                          </span>
                        )
                      )}
                    </p>
                    <p className="text-xs text-zinc-500 truncate">
                      {c.set_name}{c.card_number ? ` · #${c.card_number}` : ""}{c.is_graded ? ` · ${c.grading_company} ${c.grade}` : ` · ${c.condition}`}
                    </p>
                  </div>
                  <div className="text-sm font-mono-plex text-emerald-300 shrink-0">
                    {c.current_price?.toFixed(2)}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="sticky bottom-4 mt-4">
          <Button
            data-testid="list-ebay-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full h-12 text-sm"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating drafts…</>
              : selectedIds.size === 0 ? "Pick at least one card"
              : `Create ${selectedIds.size} draft${selectedIds.size === 1 ? "" : "s"} in your eBay Seller Hub`}
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div data-testid="list-ebay-results" className="mt-6 border border-white/10 bg-[#111113] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium">Results</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Successful drafts appear in your Seller Hub under <span className="text-zinc-300">Listings → Not started</span>. Look for SKUs starting with <span className="font-mono-plex">CARDARK-</span>.
                </p>
              </div>
              <a href={results.seller_hub_url} target="_blank" rel="noopener noreferrer"
                 data-testid="list-ebay-seller-hub-link"
                 className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1 shrink-0">
                Open Seller Hub <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="space-y-1.5 text-xs">
              {(results.results || []).map((r) => (
                <div key={r.card_id} className="flex items-center gap-2 py-1 border-b border-white/5">
                  {r.status === "draft_created"
                    ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    : <AlertTriangle className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />}
                  <div className="flex-1 truncate">
                    <span className="text-white">{r.name}</span>
                    {r.status === "draft_created" && (
                      <span className="text-zinc-500 ml-2">
                        · {r.currency} {r.price?.toFixed(2)} · offer <span className="font-mono-plex">{r.offer_id}</span>
                      </span>
                    )}
                    {r.error && <span className="text-amber-300 ml-2">— {r.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
